/**
 * CouponsPanel — admin CRUD for discount coupons.
 * Talks to /api/admin/coupons (list/create/patch/delete).
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, RefreshCw, Loader2, Percent, DollarSign } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full bg-transparent border border-hairline rounded-lg px-3 py-2.5 text-sm text-forest focus:outline-none focus:border-forest transition-colors";

const empty = {
  code: "",
  kind: "percent",
  value: 10,
  currency: "USD",
  min_subtotal: 0,
  max_uses: "",
  expires_at: "",
  active: true,
  description: "",
};

export const CouponsPanel = ({ authAxios }) => {
  const api = useMemo(() => authAxios(), [authAxios]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e?.preventDefault();
    if (saving) return;
    const payload = {
      code: form.code.trim().toUpperCase(),
      kind: form.kind,
      value: Number(form.value),
      currency: form.kind === "amount" ? (form.currency || "").toUpperCase() : undefined,
      min_subtotal: Number(form.min_subtotal) || 0,
      max_uses: form.max_uses === "" ? undefined : Number(form.max_uses),
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
      active: !!form.active,
      description: form.description?.trim() || "",
    };
    if (!payload.code || payload.code.length < 2) return toast.error("Code required");
    if (!payload.value || payload.value <= 0) return toast.error("Value must be > 0");
    if (payload.kind === "percent" && payload.value > 100) return toast.error("Percent must be 0–100");
    if (payload.kind === "amount" && !payload.currency) return toast.error("Amount coupons need a currency");
    setSaving(true);
    try {
      await api.post("/admin/coupons", payload);
      toast.success(`Coupon ${payload.code} created`);
      setForm(empty);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/coupons/${row.code}`, { active: !row.active });
      await load();
    } catch { toast.error("Toggle failed"); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete coupon ${row.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${row.code}`);
      toast.success(`Deleted ${row.code}`);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="coupons-panel">
      {/* Create form */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-hairline rounded-lg p-6 md:p-8 bg-white"
      >
        <header className="mb-5">
          <div className="overline text-champagne">§ New coupon</div>
          <h3 className="font-display text-forest text-2xl leading-none mt-1">Create a discount code</h3>
          <p className="text-forest/60 text-sm mt-1">Percent works across every currency. "Amount" coupons only apply when the buyer's currency matches.</p>
        </header>
        <form className="grid grid-cols-12 gap-4" onSubmit={create}>
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-forest/60 mb-1.5">Code</div>
            <input className={inputCls} value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="coupon-code" />
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-forest/60 mb-1.5">Type</div>
            <select className={inputCls} value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="coupon-kind">
              <option value="percent">Percent %</option>
              <option value="amount">Amount</option>
            </select>
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-forest/60 mb-1.5">Value</div>
            <input type="number" min="0" step="0.01" className={inputCls} value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="coupon-value" />
          </div>
          {form.kind === "amount" && (
            <div className="col-span-6 md:col-span-2">
              <div className="overline text-forest/60 mb-1.5">Currency</div>
              <select className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="coupon-currency">
                {["USD","INR","GBP","EUR","AUD","AED","SGD","CAD","JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-forest/60 mb-1.5">Min. subtotal</div>
            <input type="number" min="0" step="0.01" className={inputCls} value={form.min_subtotal} onChange={(e) => set("min_subtotal", e.target.value)} data-testid="coupon-min" />
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-forest/60 mb-1.5">Max uses (optional)</div>
            <input type="number" min="1" className={inputCls} value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} placeholder="Unlimited" data-testid="coupon-maxuses" />
          </div>
          <div className="col-span-6 md:col-span-4">
            <div className="overline text-forest/60 mb-1.5">Expires (optional)</div>
            <input type="datetime-local" className={inputCls} value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="coupon-expires" />
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-forest/60 mb-1.5">Description (shown internally only)</div>
            <input className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Welcome offer for new US customers" data-testid="coupon-desc" />
          </div>
          <div className="col-span-12 flex items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-forest/70 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-forest" data-testid="coupon-active" />
              Active immediately
            </label>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 overline text-white bg-forest rounded-full px-5 py-2.5 disabled:opacity-40"
              data-testid="coupon-create-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" strokeWidth={1.8} /> : <Plus size={12} strokeWidth={1.8} />}
              Create coupon
            </button>
          </div>
        </form>
      </motion.section>

      {/* Existing list */}
      <div className="border border-hairline rounded-lg overflow-hidden bg-white">
        <header className="px-6 py-4 border-b border-hairline flex items-center justify-between bg-cream/40">
          <div>
            <div className="overline text-champagne">§ Active coupons</div>
            <p className="text-forest/60 text-sm mt-0.5">{rows.length} coupon{rows.length === 1 ? "" : "s"} total</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 overline text-forest/70 hover:text-forest text-xs border border-hairline rounded-full px-3 py-1.5"
            data-testid="coupons-reload"
          >
            <RefreshCw size={11} strokeWidth={1.6} /> Reload
          </button>
        </header>
        {loading ? (
          <div className="py-16 flex items-center justify-center text-forest/60"><Loader2 size={14} className="animate-spin mr-2" strokeWidth={1.8} /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-forest/50 text-sm italic">No coupons yet — create one above.</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-hairline text-forest/60">
                <th className="overline text-[10px] py-3 px-6">Code</th>
                <th className="overline text-[10px] py-3 pr-4">Discount</th>
                <th className="overline text-[10px] py-3 pr-4">Min · Currency</th>
                <th className="overline text-[10px] py-3 pr-4">Uses</th>
                <th className="overline text-[10px] py-3 pr-4">Expires</th>
                <th className="overline text-[10px] py-3 pr-4">Status</th>
                <th className="overline text-[10px] py-3 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className="border-b border-hairline/60" data-testid={`coupon-row-${r.code}`}>
                  <td className="py-4 px-6 font-display text-forest text-lg tracking-wider">{r.code}</td>
                  <td className="py-4 pr-4 text-forest">
                    {r.kind === "percent" ? (
                      <span className="inline-flex items-center gap-1 tabnum"><Percent size={12} strokeWidth={1.8} /> {r.value}%</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 tabnum"><DollarSign size={12} strokeWidth={1.8} /> {r.value} {r.currency}</span>
                    )}
                  </td>
                  <td className="py-4 pr-4 text-forest/70 tabnum">{r.min_subtotal || 0} {r.currency || ""}</td>
                  <td className="py-4 pr-4 text-forest/70 tabnum">{r.uses || 0}{r.max_uses ? ` / ${r.max_uses}` : ""}</td>
                  <td className="py-4 pr-4 text-forest/70 text-xs">{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="py-4 pr-4">
                    <button
                      type="button"
                      onClick={() => toggle(r)}
                      className={`overline text-[10px] px-2.5 py-1 rounded-full border ${r.active ? "text-forest border-forest" : "text-forest/40 border-hairline"}`}
                      data-testid={`coupon-toggle-${r.code}`}
                    >
                      {r.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      type="button"
                      onClick={() => del(r)}
                      className="p-2 text-forest/60 hover:text-red-600"
                      aria-label="Delete"
                      data-testid={`coupon-delete-${r.code}`}
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CouponsPanel;
